import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BrandMark } from "@/components/BrandMark";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export const Login = () => {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("jziwahwa@gmail.com");
  const [password, setPassword] = useState("Cadastre360!");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const user = await login(email, password);
      toast.success("Sovereign session established");
      // Additive role-based landing redirect. Falls back to /app for
      // legacy roles to preserve prior behaviour.
      const LANDING = {
        sovereign_admin: "/app",
        minister_executive: "/app/enterprise-intelligence",
        permanent_secretary: "/app/performance",
        cadastre_administrator: "/app",
        mining_commissioner: "/app/mining",
        survey_officer: "/app/survey",
        mining_inspector: "/app/inspections",
        licensing_officer: "/app/applications",
        data_migration_officer: "/app/mining-migration",
        migration_approver: "/app/mining-migration",
        finance_officer: "/app/revenue",
        records_officer: "/app/digitisation",
        provincial_director: "/app/enterprise-intelligence",
        applicant: "/app/portal",
        legacy_claim_holder: "/app/mining-migration/claimants",
        licensed_surveyor: "/app/mining-migration/surveyors",
        cadastre_auditor: "/app/audit-register",
      };
      nav(user?.landing_path || LANDING[user?.role] || "/app");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const quickUsers = [
    { label: "Sovereign Admin", email: "jziwahwa@gmail.com", pw: "Cadastre360!" },
    { label: "Mining Commissioner", email: "mining.commissioner@cadastre360.zw", pw: "Demo!2026" },
    { label: "Land Registrar", email: "land.registrar@cadastre360.zw", pw: "Demo!2026" },
    { label: "Surveyor-General", email: "surveyor.general@cadastre360.zw", pw: "Demo!2026" },
    { label: "National Executive", email: "executive@cadastre360.zw", pw: "Demo!2026" },
    { label: "Public Applicant", email: "applicant@example.com", pw: "Demo!2026" },
  ];

  // Additive: Ministry demonstration tenant users (MOMMD-C360-DEMO).
  // Passwords are held in the owner-only Demonstration Credential Register
  // and are NOT embedded here — the owner enters them at the login prompt.
  const ministryUsers = [
    "minister@demo.cadastre360.thomdi.com",
    "ps@demo.cadastre360.thomdi.com",
    "admin@demo.cadastre360.thomdi.com",
    "registrar@demo.cadastre360.thomdi.com",
    "survey.officer@demo.cadastre360.thomdi.com",
    "inspector@demo.cadastre360.thomdi.com",
    "licensing@demo.cadastre360.thomdi.com",
    "migration@demo.cadastre360.thomdi.com",
    "migration.approver@demo.cadastre360.thomdi.com",
    "finance@demo.cadastre360.thomdi.com",
    "records@demo.cadastre360.thomdi.com",
    "provincial.director@demo.cadastre360.thomdi.com",
    "applicant@demo.cadastre360.thomdi.com",
    "claimholder@demo.cadastre360.thomdi.com",
    "licensed.surveyor@demo.cadastre360.thomdi.com",
    "auditor@demo.cadastre360.thomdi.com",
  ];

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 relative overflow-hidden">
      {/* Left / Brand column */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 border-r cad-hairline">
        <div className="cad-topo"></div>
        <div className="cad-vein"></div>
        <div className="relative z-10 flex items-center">
          <BrandMark size={212} />
        </div>
        <div className="relative z-10">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Sovereign Command Access</div>
          <div className="font-display text-4xl xl:text-5xl font-bold text-[#F6F8FA] mt-3 leading-[1.05]">
            Enter the National<br />Situation Room.
          </div>
          <div className="text-[14px] text-[#B3C2CC] mt-4 max-w-md leading-relaxed">
            Cadastre360™ integrates Land, Survey, Registry, Mineral Rights, Compliance and Executive Intelligence
            into a single sovereign environment.
          </div>
        </div>
        <div className="relative z-10 font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#5F7688]">
          Built on the THOM360™ Enterprise Platform Framework
        </div>
      </div>

      {/* Right / Form column */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-10 flex items-center">
            <BrandMark size={128} />
          </div>
          <div className="cad-panel p-7">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#C99A2E]">Authorised Access</div>
            <div className="font-display text-2xl font-bold mt-2 text-[#F6F8FA]">Sign in</div>
            <form onSubmit={submit} className="space-y-4 mt-5" data-testid="login-form">
              <div>
                <label className="block text-[11px] font-mono-cad uppercase tracking-[0.18em] text-[#B3C2CC] mb-1">Email</label>
                <input
                  data-testid="login-email"
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-3 py-2.5 text-[14px] text-[#F6F8FA] focus:ring-2 focus:ring-[#C99A2E] focus:border-transparent outline-none"
                />
              </div>
              <div>
                <label className="block text-[11px] font-mono-cad uppercase tracking-[0.18em] text-[#B3C2CC] mb-1">Password</label>
                <input
                  data-testid="login-password"
                  type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-3 py-2.5 text-[14px] text-[#F6F8FA] focus:ring-2 focus:ring-[#C99A2E] focus:border-transparent outline-none"
                />
              </div>
              {err && <div className="text-[13px] text-[#E67E7E]" data-testid="login-error">{err}</div>}
              <button type="submit" disabled={busy} data-testid="login-submit"
                      className="cad-btn-primary w-full py-3 rounded-sm inline-flex items-center justify-center gap-2 disabled:opacity-60">
                {busy && <Loader2 size={16} className="animate-spin" />}
                {busy ? "Authenticating" : "Enter Cadastre360"}
              </button>
              <div className="text-[12px] text-[#B3C2CC]">
                New applicant?{" "}
                <Link to="/register" className="text-[#C99A2E] hover:underline">Create account</Link>
                <span className="mx-2 text-[#415363]">·</span>
                <Link to="/portal" className="text-[#7CBFDC] hover:underline">Public portal</Link>
              </div>
            </form>
          </div>

          <div className="mt-6 cad-panel p-4">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#7C93A5] mb-2">Demonstration accounts</div>
            <div className="grid grid-cols-2 gap-2">
              {quickUsers.map((u) => (
                <button
                  key={u.email}
                  data-testid={`quick-${u.email}`}
                  onClick={() => { setEmail(u.email); setPassword(u.pw); }}
                  className="text-left cad-btn-outline text-[11px] px-2.5 py-2 rounded-sm truncate"
                >{u.label}</button>
              ))}
            </div>
            <div className="mt-2 text-[10px] font-mono-cad uppercase tracking-[0.18em] text-[#5F7688]">
              All demo passwords: Demo!2026 · Owner: Cadastre360!
            </div>
            <div className="mt-3 border-t cad-hairline pt-2">
              <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#C99A2E]">Ministry demonstration tenant · MOMMD-C360-DEMO</div>
              <div className="mt-1 text-[10px] text-[#B3C2CC] leading-relaxed">
                16 role-scoped accounts under <code className="font-mono-cad text-[#DCE3E8]">@demo.cadastre360.thomdi.com</code>.
                Temporary passwords are issued through the owner-only Demonstration Credential Register.
              </div>
              <details className="mt-2">
                <summary className="text-[10px] font-mono-cad uppercase tracking-[0.18em] text-[#7CBFDC] cursor-pointer" data-testid="ministry-accounts-toggle">
                  Ministry account emails
                </summary>
                <ul className="mt-2 grid grid-cols-2 gap-y-0.5 text-[10.5px] text-[#B3C2CC] font-mono-cad">
                  {ministryUsers.map((e) => (
                    <li key={e} className="truncate">
                      <button
                        data-testid={`ministry-${e}`}
                        onClick={() => setEmail(e)}
                        className="hover:text-[#C99A2E] text-left"
                      >{e}</button>
                    </li>
                  ))}
                </ul>
              </details>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
